// src/app/resources/loading.tsx
'use client';

import React from 'react';
import {
  Box,
  Container,
  Skeleton,
  Card,
  CardContent,
  Grid,
  Paper,
} from '@mui/material';

import { withOpacity } from '@/lib/colors';
import { useColorTheme } from '@/components/ColorModeProvider';

export default function Loading() {
  const colors = useColorTheme();
  const skeletonBg = withOpacity(colors.text.primary, 0.1);
  const skeletonBgStrong = withOpacity(colors.text.primary, 0.15);

  return (
    <Box sx={{ minHeight: '100vh', background: colors.background.default }}>
      {/* Hero skeleton — height matches PageHero default. */}
      <Box
        sx={{
          minHeight: '32vh',
          display: 'flex',
          alignItems: 'center',
          background: colors.background.hero,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Skeleton variant="text" height={60} width="40%" sx={{ bgcolor: skeletonBgStrong, mx: 'auto', mb: 2 }} />
            <Skeleton variant="text" height={24} width="70%" sx={{ bgcolor: skeletonBg,        mx: 'auto' }} />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Stats skeleton */}
        <Box sx={{ display: 'flex', gap: 3, mb: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
          {Array.from({ length: 3 }, (_, index) => (
            <Paper
              key={index}
              sx={{
                background: colors.background.elevated,
                border: `1px solid ${colors.border.primary}`,
                p: 2,
                textAlign: 'center',
                minWidth: '120px',
              }}
            >
              <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: skeletonBg, mx: 'auto', mb: 1 }} />
              <Skeleton variant="text"     height={32} width="60%" sx={{ bgcolor: skeletonBgStrong, mx: 'auto', mb: 1 }} />
              <Skeleton variant="text"     height={16} width="80%" sx={{ bgcolor: skeletonBg, mx: 'auto' }} />
            </Paper>
          ))}
        </Box>

        {/* Filters skeleton */}
        <Paper
          sx={{
            background: colors.background.elevated,
            border: `1px solid ${colors.border.primary}`,
            p: 3,
            mb: 4,
          }}
        >
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}><Skeleton variant="rectangular" height={56} sx={{ bgcolor: skeletonBg }} /></Grid>
            <Grid size={{ xs: 12, md: 3 }}><Skeleton variant="rectangular" height={56} sx={{ bgcolor: skeletonBg }} /></Grid>
            <Grid size={{ xs: 12, md: 2 }}><Skeleton variant="rectangular" height={56} sx={{ bgcolor: skeletonBg }} /></Grid>
            <Grid size={{ xs: 12, md: 3 }}><Skeleton variant="rectangular" height={56} sx={{ bgcolor: skeletonBg }} /></Grid>
          </Grid>
        </Paper>

        {/* Card skeletons */}
        <Grid container spacing={3}>
          {Array.from({ length: 6 }, (_, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Card
                sx={{
                  background: colors.gradients.card,
                  border: `1px solid ${colors.border.primary}`,
                  height: 400,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Skeleton variant="rectangular" height={32} width="45%" sx={{ bgcolor: skeletonBg, borderRadius: 2 }} />
                    <Skeleton variant="rectangular" height={24} width="35%" sx={{ bgcolor: skeletonBg, borderRadius: 2 }} />
                  </Box>
                  <Skeleton variant="text"        height={28} width="85%" sx={{ bgcolor: skeletonBgStrong, mb: 1 }} />
                  <Skeleton variant="text"        height={16}             sx={{ bgcolor: skeletonBg, mb: 1 }} />
                  <Skeleton variant="text"        height={16}             sx={{ bgcolor: skeletonBg, mb: 1 }} />
                  <Skeleton variant="text"        height={16} width="70%" sx={{ bgcolor: skeletonBg, mb: 2 }} />
                  <Skeleton variant="text"        height={16} width="40%" sx={{ bgcolor: skeletonBg, mb: 1 }} />
                  <Skeleton variant="text"        height={16} width="50%" sx={{ bgcolor: skeletonBg, mb: 1 }} />
                  <Skeleton variant="text"        height={16} width="45%" sx={{ bgcolor: skeletonBg, mb: 2 }} />
                  <Skeleton variant="rectangular" height={24} width="30%" sx={{ bgcolor: skeletonBg, borderRadius: 2, mb: 2 }} />
                  <Skeleton variant="rectangular" height={36}             sx={{ bgcolor: skeletonBg, borderRadius: 1, mt: 'auto' }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
